import React, { useState } from 'react'
import { TextField, TextArea, Button, Banner } from '../ui'
import { formatTimeHorizon, childHorizonOptions } from './timeHorizon.js'

const FACET_OPTIONS = ['kpi', 'deliverables', 'criteria', 'deadline', 'milestones']
const FACET_LABELS = {
  kpi: 'KPI',
  deliverables: 'Deliverables',
  criteria: 'Criteria',
  deadline: 'Deadline',
  milestones: 'Milestones',
}

export function SubgoalForm({ onSave, onCancel, parentTimeHorizon = null }) {
  const horizonOpts = childHorizonOptions(parentTimeHorizon)
  const quarterOpts = horizonOpts.filter((o) => o.value.type === 'quarterly')
  const monthOpts = horizonOpts.filter((o) => o.value.type === 'monthly')
  const hasMultiType = quarterOpts.length > 0 && monthOpts.length > 0
  const [horizonMode, setHorizonMode] = useState(quarterOpts.length > 0 ? 'quarterly' : 'monthly')
  const [selectedQuarter, setSelectedQuarter] = useState(quarterOpts[0]?.value?.quarter || 1)
  const [selectedMonth, setSelectedMonth] = useState(monthOpts[0]?.value?.month || 1)
  const [title, setTitle] = useState('')
  const [primaryFacet, setPrimaryFacet] = useState('kpi')
  const [activeFacets, setActiveFacets] = useState(new Set(['kpi']))
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Facet data
  const [kpiDirection, setKpiDirection] = useState('up')
  const [kpiCurrent, setKpiCurrent] = useState('')
  const [kpiTarget, setKpiTarget] = useState('')
  const [kpiUnit, setKpiUnit] = useState('')
  const [deliverables, setDeliverables] = useState([''])
  const [criteriaText, setCriteriaText] = useState('')
  const [deadlineDate, setDeadlineDate] = useState('')
  const [milestones, setMilestones] = useState([{ title: '', targetDate: '' }])

  function toggleFacet(facet) {
    setActiveFacets((prev) => {
      const next = new Set(prev)
      if (next.has(facet)) {
        next.delete(facet)
        if (primaryFacet === facet && next.size > 0) setPrimaryFacet([...next][0])
      } else {
        next.add(facet)
      }
      return next
    })
  }

  function selectPrimary(facet) {
    setPrimaryFacet(facet)
    setActiveFacets((prev) => new Set([...prev, facet]))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!title.trim()) { setError('Title is required.'); return }

    const input = { title: title.trim(), primaryFacet, notes: notes.trim() }
    if (horizonOpts.length > 0) {
      const year = parentTimeHorizon?.year || parentTimeHorizon?.year
      const thValue = horizonMode === 'quarterly'
        ? { type: 'quarterly', year, quarter: selectedQuarter }
        : { type: 'monthly', year, month: selectedMonth }
      input.timeHorizon = JSON.stringify(thValue)
    }

    if (activeFacets.has('kpi')) {
      const target = parseFloat(kpiTarget)
      if (isNaN(target) || target <= 0) { setError('KPI target must be a positive number.'); return }
      const current = kpiDirection === 'down' ? (parseFloat(kpiCurrent) || 0) : 0
      if (kpiDirection === 'down' && current <= target) {
        setError('For "lower is better", current value must be above the target.')
        return
      }
      input.kpi = JSON.stringify({ current, target, unit: kpiUnit.trim(), direction: kpiDirection })
    }

    if (activeFacets.has('deliverables')) {
      const items = deliverables.filter((d) => d.trim()).map((d) => ({ text: d.trim() }))
      if (items.length > 0) input.deliverables = JSON.stringify({ items })
    }

    if (activeFacets.has('criteria') && criteriaText.trim()) {
      input.criteria = JSON.stringify({ text: criteriaText.trim() })
    }

    if (activeFacets.has('deadline') && deadlineDate) {
      input.deadline = JSON.stringify({ date: deadlineDate })
    }

    if (activeFacets.has('milestones')) {
      const items = milestones.filter((m) => m.title.trim()).map((m) => ({
        title: m.title.trim(),
        targetDate: m.targetDate || undefined,
      }))
      if (items.length > 0) input.milestones = JSON.stringify({ items })
    }

    setSaving(true)
    try {
      await onSave(input)
    } catch (err) {
      setError(err?.message || 'Something went wrong.')
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What's this sub-goal about?"
        maxLength={100}
        required
      />

      {horizonOpts.length > 1 && (
        <div>
          <label style={periodStyles.label}>Period</label>
          <div style={periodStyles.row}>
            {hasMultiType && (
              <select
                style={periodStyles.select}
                value={horizonMode}
                onChange={(e) => setHorizonMode(e.target.value)}
                aria-label="Period type"
              >
                <option value="quarterly">Quarterly</option>
                <option value="monthly">Monthly</option>
              </select>
            )}
            {horizonMode === 'quarterly' && (
              <select
                style={periodStyles.select}
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(Number(e.target.value))}
                aria-label="Quarter"
              >
                {quarterOpts.map((o) => (
                  <option key={o.value.quarter} value={o.value.quarter}>{o.label}</option>
                ))}
              </select>
            )}
            {horizonMode === 'monthly' && (
              <select
                style={periodStyles.select}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                aria-label="Month"
              >
                {monthOpts.map((o) => (
                  <option key={o.value.month} value={o.value.month}>{o.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
      {horizonOpts.length === 1 && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          Period: {horizonOpts[0].label}
        </div>
      )}

      <div className="field">
        <label className="label">Primary tracking type</label>
        <div style={styles.radioGroup}>
          {FACET_OPTIONS.map((f) => (
            <label key={f} style={styles.radio}>
              <input
                type="radio"
                name="primaryFacet"
                checked={primaryFacet === f}
                onChange={() => selectPrimary(f)}
              />
              <span>{FACET_LABELS[f]}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="label">Add facets (optional)</label>
        <div style={styles.chipBar}>
          {FACET_OPTIONS.map((f) => (
            <button
              key={f}
              type="button"
              style={{ ...styles.chip, ...(activeFacets.has(f) ? styles.chipActive : {}) }}
              onClick={() => toggleFacet(f)}
              aria-pressed={activeFacets.has(f)}
            >
              {activeFacets.has(f) ? '✓ ' : '+ '}{FACET_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      {/* KPI inputs */}
      {activeFacets.has('kpi') && (
        <div style={styles.facetSection}>
          <span style={styles.facetTitle}>KPI</span>
          <div style={styles.row}>
            <label style={styles.radio}>
              <input type="radio" name="kpiDir" checked={kpiDirection === 'up'} onChange={() => setKpiDirection('up')} />
              <span>Higher is better</span>
            </label>
            <label style={styles.radio}>
              <input type="radio" name="kpiDir" checked={kpiDirection === 'down'} onChange={() => setKpiDirection('down')} />
              <span>Lower is better</span>
            </label>
          </div>
          <div style={styles.row}>
            {kpiDirection === 'down' && (
              <div style={{ flex: 1 }}>
                <TextField
                  type="number"
                  value={kpiCurrent}
                  onChange={(e) => setKpiCurrent(e.target.value)}
                  placeholder="Current value"
                />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <TextField
                type="number"
                value={kpiTarget}
                onChange={(e) => setKpiTarget(e.target.value)}
                placeholder="Target"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                value={kpiUnit}
                onChange={(e) => setKpiUnit(e.target.value)}
                placeholder="Unit (e.g. reviews)"
                maxLength={30}
              />
            </div>
          </div>
        </div>
      )}

      {/* Deliverables inputs */}
      {activeFacets.has('deliverables') && (
        <div style={styles.facetSection}>
          <span style={styles.facetTitle}>Deliverables</span>
          {deliverables.map((d, i) => (
            <div key={i} style={styles.row}>
              <div style={{ flex: 1 }}>
                <TextField
                  value={d}
                  onChange={(e) => {
                    const next = [...deliverables]
                    next[i] = e.target.value
                    setDeliverables(next)
                  }}
                  placeholder="Deliverable item"
                />
              </div>
              <button
                type="button"
                style={styles.removeBtn}
                aria-label="Remove deliverable"
                onClick={() => setDeliverables(deliverables.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDeliverables([...deliverables, ''])}
          >
            + Add deliverable
          </Button>
        </div>
      )}

      {/* Criteria input */}
      {activeFacets.has('criteria') && (
        <div style={styles.facetSection}>
          <span style={styles.facetTitle}>Completion criteria</span>
          <TextArea
            value={criteriaText}
            onChange={(e) => setCriteriaText(e.target.value)}
            placeholder="Done when..."
            maxLength={500}
            rows={2}
          />
        </div>
      )}

      {/* Deadline input */}
      {activeFacets.has('deadline') && (
        <div style={styles.facetSection}>
          <span style={styles.facetTitle}>Deadline</span>
          <TextField
            type="date"
            value={deadlineDate}
            onChange={(e) => setDeadlineDate(e.target.value)}
          />
        </div>
      )}

      {/* Milestones inputs */}
      {activeFacets.has('milestones') && (
        <div style={styles.facetSection}>
          <span style={styles.facetTitle}>Milestones</span>
          {milestones.map((m, i) => (
            <div key={i} style={styles.row}>
              <div style={{ flex: 1 }}>
                <TextField
                  value={m.title}
                  onChange={(e) => {
                    const next = [...milestones]
                    next[i] = { ...next[i], title: e.target.value }
                    setMilestones(next)
                  }}
                  placeholder="Milestone title"
                />
              </div>
              <div style={{ width: 140 }}>
                <TextField
                  type="date"
                  value={m.targetDate}
                  onChange={(e) => {
                    const next = [...milestones]
                    next[i] = { ...next[i], targetDate: e.target.value }
                    setMilestones(next)
                  }}
                />
              </div>
              <button
                type="button"
                style={styles.removeBtn}
                aria-label="Remove milestone"
                onClick={() => setMilestones(milestones.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setMilestones([...milestones, { title: '', targetDate: '' }])}
          >
            + Add milestone
          </Button>
        </div>
      )}

      <TextArea
        label="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional notes"
        maxLength={1000}
        rows={2}
      />

      {error ? <Banner tone="error">{error}</Banner> : null}

      <div style={styles.actions}>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" loading={saving} loadingLabel="Creating…">Create</Button>
      </div>
    </form>
  )
}

const periodStyles = {
  label: { fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' },
  row: { display: 'flex', gap: '8px', alignItems: 'center' },
  select: {
    padding: '6px 8px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
}

const styles = {
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' },
  row: { display: 'flex', gap: 'var(--space-2)', alignItems: 'center' },
  radioGroup: { display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' },
  radio: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-1)',
    fontSize: 12,
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  chipBar: { display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' },
  // Multi-select facet chips. The Soundcheck primitive library has no
  // toggleable-chip primitive yet (Pill is read-only); these stay inline
  // until one is added. Tracked as a follow-up.
  chip: {
    fontSize: 11,
    padding: '3px 10px',
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--border)',
    background: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  chipActive: {
    background: 'var(--text-primary)',
    color: '#fff',
    border: '1px solid var(--text-primary)',
    fontWeight: 500,
  },
  facetSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
    padding: 'var(--space-3)',
    background: 'var(--locked-bg)',
    borderRadius: 'var(--radius)',
  },
  facetTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    color: 'var(--text-secondary)',
    opacity: 0.6,
    padding: '2px 4px',
  },
  addBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    color: 'var(--text-secondary)',
    textAlign: 'left',
    padding: '2px 0',
  },
  actions: { display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' },
}
